































import Vue from 'vue';
import ReportTable, { ColumnConfig } from '@/components/ReportTable.vue';
import ReportControls, { ReportSpecByWeek, Account } from '@/components/ReportControls.vue';
import axios from 'axios';
import XLSX from 'xlsx';

const AVG = Intl.NumberFormat('en-GB', {
  style: 'decimal',
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
  useGrouping: true,
});
const CURRENCY = Intl.NumberFormat('en-GB', {style: 'decimal', maximumFractionDigits: 0, useGrouping: true});

export default Vue.extend({
  name: 'PostcodeProductMix',
  components: {ReportControls, ReportTable},
  data() {
    return {
      loading: true,
      spec: {} as ReportSpecByWeek,
      measure: 'BU' as 'BU' | 'IMR',
      networkHeaders: [],
      generalHeaders: [],
      network: [],
      general: [],
      networkBreakdowns: {} as { [postcode: string]: Array<Array<string | number>> },
      generalBreakdowns: {} as { [postcode: string]: Array<Array<string | number>> },
    };
  },
  computed: {
    networkUrl(): string {
      const BASE_URL = (process.env.NODE_ENV === 'production') ? '/api' : '//localhost:8000';
      const {depot, from, to} = this.spec;
      return `${BASE_URL}/reports/postcode-product-mix/${depot}/${from.year}/${from.week}/${to.year}/${to.week}/`;
    },
    generalUrl(): string {
      const BASE_URL = (process.env.NODE_ENV === 'production') ? '/api' : '//localhost:8000';
      const {depot, from, to} = this.spec;
      return `${BASE_URL}/reports/postcode-product-mix-general/${depot}/${from.year}/${from.week}/${to.year}/${to.week}/`;
    },
    generalColumnConfig(): Array<ColumnConfig<string | number>> {
      const format: ColumnConfig<number> = this.measure === 'IMR' ? {
        format: 'currency',
      } : {};
      const confs = [{}];
      for (const _ of this.networkHeaders.slice(1)) {
        confs.push(format);
      }
      return confs;
    },
    networkColumnConfig(): Array<ColumnConfig<string | number>> {
      return this.generalColumnConfig.concat({hidden: true});
    },
    networkExtraCols(): { [header: string]: (row: number[]) => string | number } {
      const avgFormatter = CURRENCY;

      function totalPallets(row: number[]): number {
        return row.slice(0, row.length - 1).reduce((a, b) => a + b, 0);
      }

      return {
        'Total': totalPallets,
        'Avg. IMR': (row: number[]) => {
          const tot = totalPallets(row);
          return tot === 0 ? '-' : avgFormatter.format(row[row.length - 1] / totalPallets(row));
        },
      };
    },
    generalExtraCols(): { [header: string]: (row: number[]) => string | number } {

      function totalPallets(row: number[]): number {
        return row.slice(0, row.length - 1).reduce((a, b) => a + b, 0);
      }

      return {
        Total: totalPallets,
      };
    },
  },
  watch: {
    async spec() {
      this.loading = true;
      await Promise.allSettled([this.loadNetwork(), this.loadGeneral()]);
      this.loading = false;
    },
    async measure() {
      this.loading = true;
      await Promise.allSettled([this.loadNetwork(), this.loadGeneral()]);
      this.loading = false;
    },
  },
  methods: {
    async loadNetwork() {
      const response = await axios.get(this.networkUrl, {
        params: {
          account_codes: this.spec.accountCodes.flatMap((a: Account) => a.codes),
          measure: this.measure,
        },
      });
      this.networkHeaders = response.data.headers;
      this.network = response.data.results.currentWeek;
    },
    async loadGeneral() {
      const response = await axios.get(this.generalUrl, {
        params: {
          account_codes: this.spec.accountCodes.flatMap((a: Account) => a.codes),
          measure: this.measure,
        },
      });
      this.generalHeaders = response.data.headers;
      this.general = response.data.results.currentWeek;
    },
    download() {
      const wb = XLSX.utils.book_new();
      const networkSheet = XLSX.utils.table_to_sheet((this.$refs.network as Vue).$el);
      const generalSheet = XLSX.utils.table_to_sheet((this.$refs.general as Vue).$el);
      XLSX.utils.book_append_sheet(wb, networkSheet, 'Network');
      XLSX.utils.book_append_sheet(wb, generalSheet, 'General');
      const {depot, from, to} = this.spec;
      XLSX.writeFile(wb, `Postcode Product Mix for Depot ${depot} - ${from.year}${to.week}
      to ${to.year}${to.week}.xlsx`);
    },
    async toggleBreakdown(postcode: { key: string, sort?: { index: number, direction: 'ASC' | 'DESC' } }) {
      const idx = postcode.sort?.index ?? 0;
      const descending = postcode.sort?.direction === 'DESC';

      function sortFunc(a: Array<string | number>, b: Array<string | number>) {
        let first;
        let second;
        if (idx >= a.length) {
          // Sort by total
          const aTotal = a.slice(1).reduce((x, y) => (x as number) + (y as number), 0);
          const bTotal = b.slice(1).reduce((x, y) => (x as number) + (y as number), 0);
          first = descending ? bTotal : aTotal;
          second = descending ? aTotal : bTotal;
        } else {
          first = descending ? b[idx] : a[idx];
          second = descending ? a[idx] : b[idx];
        }
        if (typeof first === 'string') {
          return first.localeCompare(second as string, undefined, {numeric: true});
        } else {
          return first - (second as number);
        }
      }

      const getNetworkBreakdowns = async () => {
        if (postcode.key in this.networkBreakdowns) {
          this.$delete(this.networkBreakdowns, postcode.key);
        } else {
          const response = await axios.get(this.networkUrl, {
            params: {
              account_codes: this.spec.accountCodes.flatMap((a: Account) => a.codes),
              measure: this.measure,
              pc: postcode.key,
            },
          });
          response.data.results.currentWeek.sort(sortFunc);
          this.$set(this.networkBreakdowns, postcode.key, response.data.results.currentWeek);
        }
      };
      const getGeneralBreakdowns = async () => {
        if (postcode.key in this.generalBreakdowns) {
          this.$delete(this.generalBreakdowns, postcode.key);
        } else {
          const response = await axios.get(this.generalUrl, {
            params: {
              account_codes: this.spec.accountCodes.flatMap((a: Account) => a.codes),
              measure: this.measure,
              pc: postcode.key,
            },
          });
          response.data.results.currentWeek.sort(sortFunc);
          this.$set(this.generalBreakdowns, postcode.key, response.data.results.currentWeek);
        }
      };

      await Promise.allSettled([getNetworkBreakdowns(), getGeneralBreakdowns()]);
    },
  },
});
